/**
 * The tabbed-body branch of `RecordBody` — the two-zone automation layout
 * (top zone: description + read-only metadata, everything else behind tabs).
 * Extracted purely to keep `RecordBody.tsx` under the project's line-count
 * convention; behavior is identical to the branch it replaced. Every prop is
 * a value `RecordBody` already computed via `useMemo`/`useState` (or read
 * from context), so this stays `React.memo`-stable across `RecordBody`
 * renders that don't touch the tabbed state.
 */
import React, { type ComponentType, type ReactElement } from 'react';

import { CodeBlock } from '@/lib/base/code';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';
import type { CrmObject, EntityTypeName } from '@/types/crm';
import { cx } from '@/utils/cx';

import type { RecordViewObject } from './contexts/RecordViewContext';
import { RECORD_BODY_FULL_BLEED, RECORD_BODY_GUTTER } from './record-layout';
import type { SlideoutTabId } from './registry/entityTypeRegistry';
import { AutomationTestCasesSection } from './sections/automation-test-cases-section';
import { OverviewSection } from './sections/overview-section';
import { RecordMetaSection } from './sections/record-meta-section';
import { SummarySection } from './sections/summary-section';
import { TimelineSection } from './sections/timeline-section';
import { TypeSpecificSection } from './sections/type-specific-section';
import type { EventTypeName } from './timeline/types';
import type { RecordSectionSet, SaveBarState } from './types';
import { cleanScriptForDisplay } from './utils/scriptSource';

/**
 * What the Runs tab shows: one ahoy_automation_ran record per automation run
 * (event trigger, clock fire, manual run-now, or guard skip), carrying
 * structured status/provenance/category plus the run summary, and one
 * ahoy_agent_ran record per agent autonomy evaluation. Automation
 * records and agent records never co-occur on one entity, so this shared,
 * locked list is safe without a per-entity override. ahoy_event_processed is
 * the deprecated automation predecessor — no new rows are written, but
 * existing rows must keep appearing in run history.
 */
const RUN_EVENT_TYPES: readonly EventTypeName[] = [
  'ahoy_automation_ran',
  'ahoy_event_processed',
  'ahoy_agent_ran',
];

interface RecordBodyTabbedContentProps {
  object: RecordViewObject;
  enabled: RecordSectionSet;
  showRunFooter: boolean;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  tabItems: { id: SlideoutTabId; label: string }[];
  slideoutTabs: readonly SlideoutTabId[];
  showSettings: boolean;
  setSaveBarState: (state: SaveBarState | null) => void;
  entityId: string;
  orgIdStr: string;
  entityTypeName: EntityTypeName;
  scriptSource: string;
  Documentation: ComponentType<{ object: CrmObject }> | undefined;
  recordMetaVariant?: 'rows' | 'inline';
  recordMetaCreatorAssociationTypes?: readonly string[];
}

export const RecordBodyTabbedContent = React.memo(
  function RecordBodyTabbedContent({
    object,
    enabled,
    showRunFooter,
    activeTab,
    setActiveTab,
    tabItems,
    slideoutTabs,
    showSettings,
    setSaveBarState,
    entityId,
    orgIdStr,
    entityTypeName,
    scriptSource,
    Documentation,
    recordMetaVariant,
    recordMetaCreatorAssociationTypes,
  }: RecordBodyTabbedContentProps): ReactElement {
    return (
      <div
        className={`${RECORD_BODY_GUTTER} py-3 ${
          showRunFooter ? 'pb-4' : 'pb-24'
        }`}
      >
        {/* Top zone, per the design: the DESCRIPTION line under the title,
            then the read-only metadata block (Created by / Created /
            Updated — status lives in the sub-header), then a full-width
            divider, then the pill tabs. The metadata is gated on
            `enabled.timeline` because created-by derives from a timeline
            fetch — an unsaved builder draft has no server record to ask
            (the same reason the timeline flag exists at all). */}
        {enabled.summary && (
          <div className='pb-4'>
            <SummarySection object={object} />
          </div>
        )}
        {enabled.timeline && (
          <>
            <RecordMetaSection
              object={object}
              variant={recordMetaVariant}
              creatorAssociationTypes={recordMetaCreatorAssociationTypes}
            />
            <div
              className={`border-t border-border-secondary mb-3 ${RECORD_BODY_FULL_BLEED}`}
            />
          </>
        )}
        <HorizontalTabs
          tabs={tabItems}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          idPrefix='record-tab'
          variant='pill'
        />
        {/* Every panel stays MOUNTED and toggles visibility — unmounting
            the Settings panel on a tab switch would throw away edit state
            mid-edit and detach the SaveBar producer. (`<Activity>` would
            pause exactly that producer effect, so `hidden` is the right
            tool here — docs/claude/REACT_19_PATTERNS.md § 6.) */}
        <div
          id='record-tab-panel-overview'
          role='tabpanel'
          className={cx(
            'flex flex-col gap-4 pt-4',
            activeTab !== 'overview' && 'hidden'
          )}
        >
          {/* The Overview tab: triggers, the configurable fields with
              SaveBar wiring, then the long-form Documentation prose
              (which renders nothing when empty). Both the settings and
              documentation blocks render HEADERLESS — the tab itself is
              the heading; per-block "Settings" / "Documentation" labels
              only added chrome between the chips and the values. */}
          {enabled.typeSpecific && <TypeSpecificSection object={object} />}
          {showSettings && (
            <OverviewSection
              object={object}
              onSaveStateChange={setSaveBarState}
              hideHeader
            />
          )}
          {Documentation && <Documentation object={object} />}
        </div>
        {slideoutTabs.includes('code') && (
          <div
            id='record-tab-panel-code'
            role='tabpanel'
            className={cx('pt-4', activeTab !== 'code' && 'hidden')}
          >
            {/* Cleaned for reading: imports and the run() signature are
                the same on every automation — only the body says what
                THIS one does. */}
            <CodeBlock source={cleanScriptForDisplay(scriptSource)} />
          </div>
        )}
        {slideoutTabs.includes('testcases') && (
          <div
            id='record-tab-panel-testcases'
            role='tabpanel'
            className={cx('pt-4', activeTab !== 'testcases' && 'hidden')}
          >
            <AutomationTestCasesSection object={object} />
          </div>
        )}
        {slideoutTabs.includes('runs') && (
          <div
            id='record-tab-panel-runs'
            role='tabpanel'
            className={cx('pt-4', activeTab !== 'runs' && 'hidden')}
          >
            {/* LOCKED to run records — no filter chrome, no widening.
                This is what makes it a Runs view rather than "the
                timeline with a filter applied". Chromeless: the tab is
                already labelled Runs. */}
            <TimelineSection
              entityId={entityId}
              orgId={orgIdStr}
              entityType={entityTypeName}
              lockedEventTypes={RUN_EVENT_TYPES}
              chromeless
              emptyMessage='No runs yet'
            />
          </div>
        )}
      </div>
    );
  }
);
